import { Link } from "react-router-dom";
import { X, ChevronRight, Scale } from "lucide-react";
import { useCompareShortlist } from "@/hooks/useCompareShortlist";

/**
 * CompareShortlistBar — floating bottom bar that appears whenever
 * the user has 1+ unis pinned via `PinToCompareButton`. Shows pinned
 * ids as removable chips and a "Compare" CTA that deep-links into
 * the existing /compare page with u1..u4 URL params.
 *
 * Mounted once inside App.jsx alongside the CookieBanner.
 */
export default function CompareShortlistBar() {
  const { ids, remove, clear, compareHref } = useCompareShortlist();
  if (!ids.length) return null;

  return (
    <div
      data-testid="compare-shortlist-bar"
      role="region"
      aria-label="Compare shortlist"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]"
    >
      <div className="flex items-center gap-3 rounded-full border border-[#7C3AED]/25 bg-white/95 backdrop-blur px-4 py-2 shadow-lg">
        <div className="flex items-center gap-1.5 pr-2 border-r border-stone-200">
          <Scale className="h-3.5 w-3.5 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#002147]">
            {ids.length} pinned
          </span>
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto">
          {ids.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full bg-[#7C3AED]/10 pl-2 pr-1 py-0.5 text-[11px] text-[#4C1D95] whitespace-nowrap"
              data-testid={`compare-chip-${id}`}
            >
              {id}
              <button
                type="button"
                onClick={() => remove(id)}
                aria-label={`Remove ${id}`}
                className="rounded-full hover:bg-[#7C3AED]/20 p-0.5"
                data-testid={`compare-chip-remove-${id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>

        <button
          type="button"
          onClick={clear}
          className="text-[10px] font-underground-sc tracking-widest text-stone-500 hover:text-stone-800 px-2"
          data-testid="compare-shortlist-clear"
        >
          Clear
        </button>

        {compareHref ? (
          <Link
            to={compareHref}
            data-testid="compare-shortlist-go"
            className="inline-flex items-center gap-1 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold px-3 py-1.5"
          >
            Compare <ChevronRight className="h-3 w-3" />
          </Link>
        ) : (
          <span className="text-[10px] font-underground-sc tracking-widest text-stone-400 px-2">
            Pin 1 more to compare
          </span>
        )}
      </div>
    </div>
  );
}
